// Layout maths for the Overview week calendar (week-one feedback §9), kept
// pure and out of the component so the fiddly parts — lane assignment,
// clipping at a week edge, the per-day cap — are unit-testable.
//
// Two layouts share one shape. 'bars' is the desktop grid: a multi-day
// period occupies a lane for the whole week row, so its segments line up
// across the cells it crosses (a lane a day doesn't use becomes a null slot,
// an invisible spacer holding the alignment). 'list' is the narrow layout,
// where the 7 columns wrap to 2 and a bar can no longer run left-to-right:
// every covered day gets its own labelled entry instead.

import { calendarDaysBetween, datesOverlap } from '../../shared/dates';
import { addDaysIso } from '../../shared/taskBuckets';

export type CalendarKind = 'shoot' | 'edit' | 'due' | 'hold';

/** One dated project item (GET /api/admin/overview/calendar). `end` equals
    `start` for a single-day item; both bounds are inclusive. */
interface CalendarItemBase {
  id: string;
  name: string;
  start: string;
  end: string;
}

export type CalendarItem = CalendarItemBase & (
  | { kind: Exclude<CalendarKind, 'hold'>; project_id: string; project_number: number | null }
  | { kind: 'hold'; hold_id: string; client_text: string | null; note: string | null
      /** 2F: set on call sheet holds (src/shared/holds.ts); absent on older fixtures. */
      project_id?: string | null; callsheet_url?: string | null; callsheet_version?: number | null }
);
export type CalendarHold = Extract<CalendarItem, { kind: 'hold' }>;

/** Items drawn in a day cell before the rest fold into "+N more". */
export const WEEK_DAY_CAP = 4;

export interface SpanSlot {
  item: CalendarItem;
  /** First cell of the run inside this week — the one carrying the label. */
  first: boolean;
  /** The run started in an earlier week, so this end takes no left cap. */
  open: boolean;
  /** This cell is the item's true last day, so the bar closes here. */
  last: boolean;
}

export interface DayEntry {
  item: CalendarItem;
  /** Position within a multi-day period ("day 2 of 5") — 'list' mode only. */
  step: { index: number; total: number } | null;
}

export interface DayCell {
  day: string;
  weekend: boolean;
  today: boolean;
  /** Lane row: a bar segment, or null for the spacer holding its place. */
  slots: (SpanSlot | null)[];
  entries: DayEntry[];
  /** Past the cap — reachable by expanding the day's "+N more". */
  overflow: DayEntry[];
}

export type WeekLayoutMode = 'bars' | 'list';

const isSpan = (item: CalendarItem): boolean => item.kind === 'hold' || item.end > item.start;

/** Mon starts of the `weeks` rows beginning at `from`. */
export function weekStartsFrom(from: string, weeks: number): string[] {
  return Array.from({ length: weeks }, (_, i) => addDaysIso(from, i * 7));
}

/** Greedy first-fit lanes over the week's visible slice of each period, so
    two runs only share a lane when they never share a day. */
function assignLanes(spans: CalendarItem[], weekStart: string, weekEnd: string): CalendarItem[][] {
  const clip = (item: CalendarItem) => ({
    start: item.start < weekStart ? weekStart : item.start,
    end: item.end > weekEnd ? weekEnd : item.end,
  });
  const lanes: CalendarItem[][] = [];
  const ordered = [...spans].sort(
    (a, b) => a.start.localeCompare(b.start) || b.end.localeCompare(a.end) || a.id.localeCompare(b.id),
  );
  for (const item of ordered) {
    const box = clip(item);
    let lane = lanes.find((l) =>
      l.every((other) => {
        const o = clip(other);
        return !datesOverlap(box.start, box.end, o.start, o.end);
      }),
    );
    if (!lane) {
      lane = [];
      lanes.push(lane);
    }
    lane.push(item);
  }
  return lanes;
}

/** Deadlines take the last visible row in a day: a period that runs all
    week still reads from its bar (or its other days), a delivery date only
    appears once and must not be the thing that falls into "+N more". */
const dueFirst = (a: DayEntry, b: DayEntry): number =>
  Number(b.item.kind === 'due') - Number(a.item.kind === 'due');

function stepOf(item: CalendarItem, day: string): DayEntry {
  if (item.end === item.start) return { item, step: null };
  return {
    item,
    step: {
      index: calendarDaysBetween(item.start, day) + 1,
      total: calendarDaysBetween(item.start, item.end) + 1,
    },
  };
}

/** The seven cells of one Mon–Sun row. */
export function layOutWeek({
  weekStart,
  items,
  today,
  mode = 'bars',
  cap = WEEK_DAY_CAP,
}: {
  weekStart: string;
  items: CalendarItem[];
  today: string;
  mode?: WeekLayoutMode;
  cap?: number;
}): DayCell[] {
  const weekEnd = addDaysIso(weekStart, 6);
  const visible = items.filter((i) => i.start <= weekEnd && i.end >= weekStart);
  const lanes = mode === 'bars' ? assignLanes(visible.filter(isSpan), weekStart, weekEnd) : [];

  return Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i)).map((day) => {
    const dow = new Date(`${day}T00:00:00`).getDay();
    const base = { day, weekend: dow === 0 || dow === 6, today: day === today };
    const covering = visible.filter((i) => i.start <= day && i.end >= day);

    if (mode === 'list') {
      const all = covering.map((item) => stepOf(item, day)).sort(dueFirst);
      return { ...base, slots: [], entries: all.slice(0, cap), overflow: all.slice(cap) };
    }

    // Lanes past the cap never draw a bar (a broken run reads worse than a
    // counted one), so their items fold into the same overflow as the
    // single-day entries that don't fit.
    const slots: (SpanSlot | null)[] = lanes.slice(0, cap).map((lane) => {
      const item = lane.find((i) => i.start <= day && i.end >= day);
      if (!item) return null;
      const firstVisible = item.start < weekStart ? weekStart : item.start;
      return {
        item,
        first: day === firstVisible,
        open: day === firstVisible && item.start < weekStart,
        last: item.end === day,
      };
    });
    while (slots.length > 0 && slots[slots.length - 1] === null) slots.pop();

    const room = Math.max(0, cap - slots.length);
    const singles = covering
      .filter((i) => !isSpan(i))
      .map((item) => stepOf(item, day))
      .sort(dueFirst);
    const dropped = lanes
      .slice(cap)
      .flatMap((lane) => lane.filter((i) => i.start <= day && i.end >= day))
      .map((item) => stepOf(item, day));

    return {
      ...base,
      slots,
      entries: singles.slice(0, room),
      overflow: [...dropped, ...singles.slice(room)],
    };
  });
}
